import React, { useEffect, useRef, useState } from 'react';

interface ProxyEditorProps {
  url: string;
  onSave: (content: string) => void;
  onClose: () => void;
}

const ProxyEditor: React.FC<ProxyEditorProps> = ({
  url,
  onSave,
  onClose
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [htmlContent, setHtmlContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchWebsiteContent();
  }, [url]);

  const fetchWebsiteContent = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Try multiple CORS proxies
      const proxies = [
        `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`,
        `https://cors-anywhere.herokuapp.com/${url}`,
        `https://thingproxy.freeboard.io/fetch/${url}`,
        `https://api.codetabs.com/v1/proxy?quest=${url}`
      ];

      let content = null;
      let proxyUsed = '';

      for (const proxy of proxies) {
        try {
          console.log(`Trying proxy: ${proxy}`);
          const response = await fetch(proxy, {
            method: 'GET',
            headers: {
              'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8',
              'Accept-Language': 'en-US,en;q=0.5',
              'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
            }
          });

          if (response.ok) {
            const data = await response.json();
            content = data.contents || data;
            proxyUsed = proxy;
            console.log(`Success with proxy: ${proxy}`);
            break;
          }
        } catch (err) {
          console.log(`Proxy failed: ${proxy}`, err);
          continue;
        }
      }

      if (!content) {
        throw new Error('All proxies failed. Cannot fetch website content.');
      }

      // Clean and process the content
      let cleanedContent = content
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove scripts
        .replace(/on\w+="[^"]*"/gi, '') // Remove event handlers
        .replace(/on\w+='[^']*'/gi, '') // Remove event handlers
        .replace(/javascript:/gi, '') // Remove javascript: protocols
        .replace(/data-src/gi, 'src') // Convert data-src to src for images
        .replace(/srcset="[^"]*"/gi, '') // Remove srcset attributes
        .replace(/sizes="[^"]*"/gi, '') // Remove sizes attributes
        .replace(/<!--[\s\S]*?-->/g, ''); // Remove HTML comments

      // Extract body content if it's a full HTML document
      if (cleanedContent.includes('<body>')) {
        const bodyMatch = cleanedContent.match(/<body[^>]*>([\s\S]*)<\/body>/i);
        if (bodyMatch) {
          cleanedContent = bodyMatch[1];
        }
      }

      setHtmlContent(cleanedContent);
      setIsLoading(false);

    } catch (err) {
      console.error('Error fetching website content:', err);
      setError(`Failed to fetch website content: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setIsLoading(false);
    }
  };

  const enableEditing = () => {
    if (!editorRef.current) return;
    
    editorRef.current.contentEditable = 'true';
    editorRef.current.style.cursor = 'text';
    editorRef.current.style.outline = '2px solid #007bff';
    editorRef.current.style.outlineOffset = '10px';
    setIsEditing(true);
  };

  const disableEditing = () => {
    if (!editorRef.current) return;
    
    editorRef.current.contentEditable = 'false';
    editorRef.current.style.cursor = 'default';
    editorRef.current.style.outline = 'none';
    setIsEditing(false);
  };

  const saveContent = () => {
    if (!editorRef.current) return;
    
    const content = editorRef.current.innerHTML;
    const fullHtml = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Edited Page - ${url}</title>
    <style>
        body { 
            font-family: Arial, sans-serif; 
            margin: 20px; 
            line-height: 1.6;
            color: #333;
        }
        img {
            max-width: 100%;
            height: auto;
        }
        a {
            color: #007bff;
            text-decoration: none;
        }
        a:hover {
            text-decoration: underline;
        }
    </style>
</head>
<body>
    ${content}
</body>
</html>`;
    
    onSave(fullHtml);
  };

  const addElement = (elementType: string) => {
    if (!editorRef.current || !isEditing) return;

    let newElement: HTMLElement;
    
    switch (elementType) {
      case 'heading':
        newElement = document.createElement('h2');
        newElement.textContent = 'New Heading';
        newElement.style.color = '#333';
        newElement.style.margin = '20px 0';
        break;
      case 'paragraph':
        newElement = document.createElement('p');
        newElement.textContent = 'New paragraph text here. Click to edit.';
        newElement.style.lineHeight = '1.6';
        newElement.style.color = '#555';
        newElement.style.margin = '15px 0';
        break;
      case 'button':
        newElement = document.createElement('button');
        newElement.textContent = 'Click Me';
        newElement.style.background = '#007bff';
        newElement.style.color = 'white';
        newElement.style.padding = '10px 20px';
        newElement.style.border = 'none';
        newElement.style.borderRadius = '5px';
        newElement.style.cursor = 'pointer';
        newElement.style.margin = '10px 0';
        break;
      case 'image':
        const imgElement = document.createElement('img');
        imgElement.src = 'https://via.placeholder.com/300x200';
        imgElement.alt = 'Placeholder Image';
        imgElement.style.maxWidth = '100%';
        imgElement.style.height = 'auto';
        imgElement.style.margin = '15px 0';
        newElement = imgElement;
        break;
      case 'divider':
        newElement = document.createElement('hr');
        newElement.style.border = '1px solid #ddd';
        newElement.style.margin = '20px 0';
        break;
      default:
        newElement = document.createElement('div');
        newElement.textContent = 'New element';
        newElement.style.padding = '10px';
        newElement.style.border = '1px solid #ddd';
        newElement.style.margin = '10px 0';
    }

    editorRef.current.appendChild(newElement);
  };

  if (isLoading) {
    return (
      <div style={{ 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f8f9fa'
      }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5>Loading Website Content...</h5>
          <p className="text-muted">{url}</p>
          <small className="text-muted">Using proxy to fetch content...</small>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f8f9fa'
      }}>
        <div className="text-center">
          <div className="text-danger mb-3">
            <i className="fas fa-exclamation-triangle fa-3x"></i>
          </div>
          <h5>Error Loading Website</h5>
          <p className="text-muted">{error}</p>
          <button className="btn btn-primary me-2" onClick={fetchWebsiteContent}>
            <i className="fas fa-redo me-1"></i>
            Try Again
          </button>
          <button className="btn btn-outline-secondary" onClick={onClose}>
            <i className="fas fa-times me-1"></i>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <div className="p-3 border-bottom bg-light">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            <i className="fas fa-globe me-2"></i>
            Proxy Editor - {url}
          </h6>
          <div>
            {!isEditing ? (
              <button className="btn btn-sm btn-primary me-2" onClick={enableEditing}>
                <i className="fas fa-edit me-1"></i>
                Enable Editing
              </button>
            ) : (
              <button className="btn btn-sm btn-secondary me-2" onClick={disableEditing}>
                <i className="fas fa-eye me-1"></i>
                Disable Editing
              </button>
            )}
            <button className="btn btn-sm btn-success me-2" onClick={saveContent}>
              <i className="fas fa-save me-1"></i>
              Save
            </button>
            <button className="btn btn-sm btn-outline-danger" onClick={onClose}>
              <i className="fas fa-times me-1"></i>
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Component Library (only when editing) */}
      {isEditing && (
        <div className="p-2 border-bottom bg-light">
          <div className="d-flex gap-2">
            <button 
              className="btn btn-sm btn-outline-secondary"
              onClick={() => addElement('heading')}
            >
              <i className="fas fa-heading me-1"></i>
              Heading
            </button>
            <button 
              className="btn btn-sm btn-outline-secondary"
              onClick={() => addElement('paragraph')}
            >
              <i className="fas fa-paragraph me-1"></i>
              Paragraph
            </button>
            <button 
              className="btn btn-sm btn-outline-secondary"
              onClick={() => addElement('button')}
            >
              <i className="fas fa-square me-1"></i>
              Button
            </button>
            <button 
              className="btn btn-sm btn-outline-secondary"
              onClick={() => addElement('image')}
            >
              <i className="fas fa-image me-1"></i>
              Image
            </button>
            <button 
              className="btn btn-sm btn-outline-secondary"
              onClick={() => addElement('divider')}
            >
              <i className="fas fa-minus me-1"></i>
              Divider
            </button>
          </div>
        </div>
      )}

      {/* Content Editor */}
      <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
        <div 
          ref={editorRef}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
          style={{
            minHeight: '100%',
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}
        />
      </div>

      {/* Editing Instructions */}
      {isEditing && (
        <div className="p-3 border-top bg-light">
          <div className="alert alert-info mb-0" role="alert">
            <i className="fas fa-info-circle me-2"></i>
            <strong>Editing Mode Active:</strong> Click on any text or element to edit it directly. 
            Use the toolbar above to add new elements. Changes are made in real-time.
          </div>
        </div>
      )}
    </div>
  );
};

export default ProxyEditor;
